import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { Select } from '@/components/ui/Select/Select';
import { Alert } from '@/components/ui/Alert/Alert';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { HStack, VStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { SettingsService } from '@/services/SettingsService';
import { ManifestPromptsService } from '@/services/ManifestPromptsService';
import { Sparkles, Loader2, Building2, RefreshCw, List, FileJson, Search, FileText, Download, FileCode, History } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import type { AISettings } from '@/services/SettingsService';
import { CventService } from '@/services/CventService';

export function AdminSettings() {
  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotificationStore();
  const service = new SettingsService();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'ai');
  const manifestPromptsService = new ManifestPromptsService();

  // Sync tab from URL
  useEffect(() => {
    if (tabParam && ['ai', 'cvent-api', 'cvent-backup', 'manifest-prompts'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const setActiveTabWithUrl = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'ai') {
      setSearchParams(new URLSearchParams());
    } else {
      setSearchParams({ tab });
    }
  };

  // Cvent API settings
  const { data: cventApiSettings, isLoading: isLoadingCventApi } = useQuery({
    queryKey: ['admin-settings', 'cvent-api'],
    queryFn: () => service.getCventApiSettings(),
  });

  const [cventApiClientId, setCventApiClientId] = useState('');
  const [cventApiClientSecret, setCventApiClientSecret] = useState('');
  const [cventApiRegion, setCventApiRegion] = useState<'na' | 'eu' | 'na-sbx'>('na');

  // Update form when data loads
  React.useEffect(() => {
    if (cventApiSettings) {
      setCventApiClientId(cventApiSettings.clientId || '');
      setCventApiRegion(cventApiSettings.region as 'na' | 'eu' | 'na-sbx' || 'na');
      // Don't set secret (it's not returned for security)
    }
  }, [cventApiSettings]);

  // Update Cvent API credentials mutation
  const updateCventApiCredentialsMutation = useMutation({
    mutationFn: () => service.updateCventApiCredentials(cventApiClientId, cventApiClientSecret, cventApiRegion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'cvent-api'] });
      success('Cvent API credentials updated successfully');
      setCventApiClientSecret(''); // Clear password field after save
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to update Cvent API credentials');
    },
  });

  // Test Cvent API connection mutation
  const testCventApiConnectionMutation = useMutation({
    mutationFn: () => apiFetch('/api/cvent/api/test-connection', { method: 'POST' }).then(r => r.json()),
    onSuccess: (data) => {
      success(data.message || 'Successfully connected to Cvent API');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to connect to Cvent API');
    },
  });

  // Fetch AI settings
  const { data: settings, isLoading: isLoadingAI } = useQuery({
    queryKey: ['admin-settings', 'ai'],
    queryFn: () => service.getAISettings(),
  });

  // Fetch Cvent settings
  const { data: cventSettings, isLoading: isLoadingCvent } = useQuery({
    queryKey: ['admin-settings', 'cvent'],
    queryFn: () => service.getCventSettings(),
  });

  // Fetch manifest prompts (only when tab is active)
  const { data: manifestPrompts, isLoading: isLoadingManifestPrompts, isError: isManifestPromptsError } = useQuery({
    queryKey: ['admin-settings', 'manifest-prompts'],
    queryFn: () => manifestPromptsService.getAll(),
    enabled: activeTab === 'manifest-prompts',
  });

  // Update enrichment model mutation
  const updateEnrichmentMutation = useMutation({
    mutationFn: (model: string) => service.updateEnrichmentModel(model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'ai'] });
      success('Enrichment model updated');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to update enrichment model');
    },
  });

  // Update research model mutation
  const updateResearchMutation = useMutation({
    mutationFn: (model: string) => service.updateResearchModel(model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'ai'] });
      success('Research model updated');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to update research model');
    },
  });

  // Manifest prompts mutations
  const updateManifestPromptMutation = useMutation({
    mutationFn: ({ key, content }: { key: 'extraction' | 'review'; content: string }) =>
      manifestPromptsService.update(key, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'manifest-prompts'] });
      success('Prompt updated (new version created)');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to update prompt');
    },
  });

  const revertManifestPromptMutation = useMutation({
    mutationFn: ({ key, version }: { key: 'extraction' | 'review'; version: number }) =>
      manifestPromptsService.revert(key, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'manifest-prompts'] });
      success('Reverted to previous version');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to revert');
    },
  });

  const seedManifestPromptsMutation = useMutation({
    mutationFn: () => manifestPromptsService.seed(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'manifest-prompts'] });
      success(data.extraction || data.review ? 'Prompts seeded' : 'Prompts already exist');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to seed');
    },
  });

  // Update Cvent browser cookies mutation
  const updateCventCookiesMutation = useMutation({
    mutationFn: (cookies: string) => service.updateCventBrowserCookies(cookies),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'cvent'] });
      success('Cvent browser cookies updated');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to update Cvent browser cookies');
    },
  });

  // Trigger Cvent login mutation
  const triggerCventLoginMutation = useMutation({
    mutationFn: () => service.triggerCventLogin(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'cvent'] });
      success(data.message || 'Cvent login successful. Cookies have been updated.');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to trigger Cvent login');
    },
  });

  // Cvent API testing
  const cventService = new CventService();
  const [rfpListResult, setRfpListResult] = useState<string | null>(null);
  const [rfpSearchString, setRfpSearchString] = useState<string>('');
  const [rfpDetailsId, setRfpDetailsId] = useState<string>('');
  const [rfpDetailsResult, setRfpDetailsResult] = useState<string | null>(null);
  const [proposalRfpId, setProposalRfpId] = useState<string>('');
  const [proposalSupplierId, setProposalSupplierId] = useState<string>('');
  const [venueResponseRfpId, setVenueResponseRfpId] = useState<string>('');
  const [venueResponseSupplierId, setVenueResponseSupplierId] = useState<string>('');
  const [venueResponseResult, setVenueResponseResult] = useState<string | null>(null);
  
  const fetchRfpListMutation = useMutation({
    mutationFn: (searchString?: string) => cventService.fetchRfpList({
      searchString: searchString || undefined,
      limit: 25,
    }),
    onSuccess: (data) => {
      setRfpListResult(JSON.stringify(data, null, 2));
      success('RFP list fetched successfully');
    },
    onError: (err: any) => {
      setRfpListResult(`Error: ${err?.message || 'Failed to fetch RFP list'}`);
      notifyError(err?.message || 'Failed to fetch RFP list');
    },
  });

  const fetchRfpDetailsMutation = useMutation({
    mutationFn: (rfpId: string) => cventService.fetchRfpDetails(rfpId, {
      includeResponseOverview: true,
      includeCustomQuestions: false,
    }),
    onSuccess: (data) => {
      setRfpDetailsResult(JSON.stringify(data, null, 2));
      success('RFP details fetched successfully');
    },
    onError: (err: any) => {
      setRfpDetailsResult(`Error: ${err?.message || 'Failed to fetch RFP details'}`);
      notifyError(err?.message || 'Failed to fetch RFP details');
    },
  });

  const fetchVenueResponseMutation = useMutation({
    mutationFn: ({ rfpId, supplierId }: { rfpId: string; supplierId: string }) =>
      cventService.fetchVenueResponse(rfpId, supplierId),
    onSuccess: (data) => {
      setVenueResponseResult(JSON.stringify(data, null, 2));
      success('Venue response fetched successfully');
    },
    onError: (err: any) => {
      setVenueResponseResult(`Error: ${err?.message || 'Failed to fetch venue response'}`);
      notifyError(err?.message || 'Failed to fetch venue response');
    },
  });

  const downloadProposalPdfMutation = useMutation({
    mutationFn: ({ rfpId, supplierId }: { rfpId: string; supplierId: string }) => 
      cventService.downloadProposalPdf(rfpId, supplierId),
    onSuccess: (blob, variables) => {
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal-${variables.rfpId}-${variables.supplierId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      success('Proposal PDF downloaded successfully');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to download proposal PDF');
    },
  });

  // Auto-save on change
  const handleEnrichmentChange = (value: string) => {
    if (value && value !== settings?.current.enrichment.model) {
      updateEnrichmentMutation.mutate(value);
    }
  };

  const handleResearchChange = (value: string) => {
    if (value && value !== settings?.current.research.model) {
      updateResearchMutation.mutate(value);
    }
  };

  const isLoading = isLoadingAI || isLoadingCvent || isLoadingCventApi;

  if (isLoading) {
    return (
      <PageContainer>
        <VStack spacing={6} className="items-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </VStack>
      </PageContainer>
    );
  }

  if (!settings) {
    return (
      <PageContainer>
        <Alert variant="error">Failed to load settings</Alert>
      </PageContainer>
    );
  }

  // Build options arrays for Select component (model name only)
  const enrichmentOptions = Object.entries(settings.available.enrichment).map(([value, config]) => ({
    value,
    label: config.name,
  }));

  const researchOptions = Object.entries(settings.available.research).map(([value, config]) => ({
    value,
    label: config.name,
  }));

  const selectedEnrichmentInfo = settings.available.enrichment[settings.current.enrichment.model];
  const selectedResearchInfo = settings.available.research[settings.current.research.model];

  return (
    <PageContainer>
      <PageHeader 
        title="Settings" 
        description="Configure system-wide settings and integrations"
      />

      <Tabs value={activeTab} onValueChange={setActiveTabWithUrl} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Model Configuration
          </TabsTrigger>
          <TabsTrigger value="cvent-api">
            <Building2 className="w-4 h-4 mr-2" />
            Cvent API
          </TabsTrigger>
          <TabsTrigger value="cvent-backup">
            <Building2 className="w-4 h-4 mr-2" />
            Cvent (Backup)
          </TabsTrigger>
          <TabsTrigger value="manifest-prompts">
            <FileCode className="w-4 h-4 mr-2" />
            Manifest Prompts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="space-y-6">
        {/* AI Model Configuration */}
        <Card>
          <CardHeader>
            <HStack spacing={2} className="items-center">
              <Sparkles className="w-5 h-5 text-primary-600" />
              <VStack spacing={0} className="flex-1">
                <CardTitle>AI Model Configuration</CardTitle>
                <CardDescription>
                  Choose which AI models to use for different tasks. Changes take effect immediately for new jobs.
                </CardDescription>
              </VStack>
            </HStack>
          </CardHeader>

          <CardContent>
            <VStack spacing={6}>
            {/* Enrichment Model */}
              <VStack spacing={3}>
              <Select
                label="AI Enrichment Model"
                hint="Model used for automatic venue enrichment (web search + data extraction)"
                value={settings.current.enrichment.model}
                onValueChange={handleEnrichmentChange}
                options={enrichmentOptions}
                disabled={updateEnrichmentMutation.isPending}
              />

              {selectedEnrichmentInfo && (
                  <Alert variant="success" title={selectedEnrichmentInfo.name}>
                    <VStack spacing={1}>
                      <p>{selectedEnrichmentInfo.description}</p>
                      <p className="text-xs">Cost: {selectedEnrichmentInfo.costPer1M} per 1M tokens</p>
                    </VStack>
                  </Alert>
              )}

              {updateEnrichmentMutation.isPending && (
                  <HStack spacing={2} className="text-sm text-neutral-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Updating model...</span>
                  </HStack>
              )}
              </VStack>

            <div className="border-t border-neutral-200" />

            {/* Research Model */}
              <VStack spacing={3}>
              <Select
                label="AI Venue Research Model"
                hint="Model used for venue research agent (multi-venue recommendations)"
                value={settings.current.research.model}
                onValueChange={handleResearchChange}
                options={researchOptions}
                disabled={updateResearchMutation.isPending}
              />

              {selectedResearchInfo && (
                  <Alert variant="success" title={selectedResearchInfo.name}>
                    <VStack spacing={1}>
                      <p>{selectedResearchInfo.description}</p>
                      <p className="text-xs">Cost: {selectedResearchInfo.costPer1M} per 1M tokens</p>
                    </VStack>
                  </Alert>
              )}

              {updateResearchMutation.isPending && (
                  <HStack spacing={2} className="text-sm text-neutral-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Updating model...</span>
                  </HStack>
              )}
              </VStack>
            </VStack>
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="cvent-api">
          {/* Cvent API Settings */}
          <Card>
            <CardHeader>
              <HStack spacing={2} className="items-center">
                <Building2 className="w-5 h-5 text-primary-600" />
                <VStack spacing={0} className="flex-1">
                  <CardTitle>Cvent REST API Configuration</CardTitle>
                  <CardDescription>
                    Configure OAuth 2.0 credentials for Cvent Planner API access. This is the primary method for Cvent integration.
                  </CardDescription>
                </VStack>
              </HStack>
            </CardHeader>

            <CardContent>
              <VStack spacing={6}>
                {/* OAuth Credentials Form */}
                <VStack spacing={4}>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Client ID
                    </label>
                    <Input
                      type="text"
                      value={cventApiClientId}
                      onChange={(e) => setCventApiClientId(e.target.value)}
                      placeholder="Your OAuth 2.0 Client ID from Cvent Developer Portal"
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Get this from <a href="https://developer.cvent.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Cvent Developer Portal</a>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Client Secret
                    </label>
                    <Input
                      type="password"
                      value={cventApiClientSecret}
                      onChange={(e) => setCventApiClientSecret(e.target.value)}
                      placeholder={cventApiSettings?.hasSecret ? "••••••••••••••••" : "Your OAuth 2.0 Client Secret"}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Secret is encrypted and stored securely. Leave blank to keep existing secret.
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Region
                    </label>
                    <Select
                      value={cventApiRegion}
                      onValueChange={(value) => setCventApiRegion(value as 'na' | 'eu' | 'na-sbx')}
                      options={[
                        { value: 'na', label: 'North America (Production)' },
                        { value: 'eu', label: 'Europe (Production)' },
                        { value: 'na-sbx', label: 'North America (Sandbox)' },
                      ]}
                    />
                  </div>

                  <HStack spacing={3}>
                    <Button
                      onClick={() => updateCventApiCredentialsMutation.mutate()}
                      disabled={!cventApiClientId || (!cventApiClientSecret && !cventApiSettings?.hasSecret) || updateCventApiCredentialsMutation.isPending}
                      isLoading={updateCventApiCredentialsMutation.isPending}
                      variant="primary"
                      size="md"
                    >
                      Save Credentials
                    </Button>

                    <Button
                      onClick={() => testCventApiConnectionMutation.mutate()}
                      disabled={!cventApiSettings?.hasSecret || testCventApiConnectionMutation.isPending}
                      isLoading={testCventApiConnectionMutation.isPending}
                      leftIcon={<RefreshCw className="w-4 h-4" />}
                      variant="secondary"
                      size="md"
                    >
                      Test Connection
                    </Button>
                  </HStack>

                  {cventApiSettings?.hasSecret && (
                    <Alert variant="success" title="Credentials Configured">
                      OAuth credentials are set and ready to use for Cvent REST API access.
                    </Alert>
                  )}

                  {!cventApiSettings?.hasSecret && (
                    <Alert variant="info" title="Configuration Required">
                      Please configure your OAuth 2.0 credentials to use the Cvent API integration.
                    </Alert>
                  )}
                </VStack>

                <div className="border-t border-neutral-200" />

                {/* Required Scopes Info */}
                <VStack spacing={3}>
                  <h3 className="text-sm font-semibold text-foreground">Required Scopes</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Your OAuth app must have these scopes enabled:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><code className="text-xs bg-neutral-100 px-1 rounded">csn-planner-rfps:read</code></li>
                      <li><code className="text-xs bg-neutral-100 px-1 rounded">csn-planner-proposals:read</code></li>
                      <li><code className="text-xs bg-neutral-100 px-1 rounded">csn-planner-messages:read</code></li>
                      <li><code className="text-xs bg-neutral-100 px-1 rounded">csn-proposals:read</code></li>
                      <li><code className="text-xs bg-neutral-100 px-1 rounded">csn-venues:read</code></li>
                    </ul>
                    <p className="mt-2">
                      Contact Cvent support if these scopes are not available in your developer portal.
                    </p>
                  </div>
                </VStack>
              </VStack>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cvent-backup">
          {/* Cvent GraphQL Settings (Backup) */}
          <Card>
            <CardHeader>
              <HStack spacing={2} className="items-center">
                <Building2 className="w-5 h-5 text-neutral-500" />
                <VStack spacing={0} className="flex-1">
                  <CardTitle>Cvent GraphQL Integration (Backup)</CardTitle>
                  <CardDescription>
                    ⚠️ Legacy integration using browser cookies. Only use if REST API has issues.
                  </CardDescription>
                </VStack>
              </HStack>
            </CardHeader>

            <CardContent>
              <VStack spacing={6}>
                {/* Run Now Button */}
                <Card variant="elevated">
                  <CardContent>
                    <HStack spacing={4} className="items-center justify-between">
                      <VStack spacing={1} className="flex-1">
                        <h3 className="text-sm font-medium text-foreground">
                          Automated Login
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Automatically log in to Cvent and extract browser cookies using Puppeteer.
                        </p>
                      </VStack>
                      <Button
                        onClick={() => triggerCventLoginMutation.mutate()}
                        disabled={triggerCventLoginMutation.isPending}
                        isLoading={triggerCventLoginMutation.isPending}
                        leftIcon={<RefreshCw className="w-4 h-4" />}
                        variant="primary"
                        size="md"
                      >
                        Run Now
                      </Button>
                    </HStack>
                  </CardContent>
                </Card>

                {/* Progress Indicator */}
                {triggerCventLoginMutation.isPending && (
                  <Alert variant="info" title="Logging in to Cvent...">
                    This may take 10-30 seconds. Please wait while we authenticate and extract cookies.
                  </Alert>
                )}

                {/* Success Message */}
                {triggerCventLoginMutation.isSuccess && (
                  <Alert variant="success" title="Login Successful">
                    Cookies have been automatically extracted and saved. They are displayed below.
                  </Alert>
                )}

                {/* Error Message */}
                {triggerCventLoginMutation.isError && (
                  <Alert variant="error" title="Login Failed">
                    {triggerCventLoginMutation.error?.message || 'Failed to log in to Cvent. Please check your credentials and try again.'}
                  </Alert>
                )}

                <VStack spacing={3}>
                  <HStack spacing={2} className="items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Browser Cookies
                    </label>
                    {cventSettings?.browserCookies && (
                      <Badge variant="secondary">
                        {cventSettings.browserCookies.split(';').length} cookies
                      </Badge>
                    )}
                  </HStack>
                  <Textarea
                    label=""
                    hint="Paste the browser cookies exported from Cvent supplier portal, or use the 'Run Now' button above to automatically extract them. Format: cookie1=value1; cookie2=value2; ..."
                    value={cventSettings?.browserCookies || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value !== cventSettings?.browserCookies) {
                        updateCventCookiesMutation.mutate(value);
                      }
                    }}
                    disabled={updateCventCookiesMutation.isPending || triggerCventLoginMutation.isPending}
                    placeholder="cvent-auth=...; csn-appointment-auth=...; rl_user_id=...; rl_group_id=..."
                    rows={6}
                    className="font-mono text-sm"
                  />
                  {updateCventCookiesMutation.isPending && (
                    <HStack spacing={2} className="text-sm text-neutral-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Updating cookies...</span>
                    </HStack>
                  )}
                  {cventSettings?.browserCookies && !triggerCventLoginMutation.isPending && (
                    <Alert variant="success" title="Cookies configured">
                      Browser cookies are set and ready to use for Cvent GraphQL API access.
                    </Alert>
                  )}
                </VStack>

                {/* API Testing Section */}
                <Card variant="bordered">
                  <CardHeader>
                    <CardTitle>API Testing</CardTitle>
                    <CardDescription>
                      Test Cvent GraphQL endpoints. Responses are displayed as JSON below.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <VStack spacing={6}>
                      {/* Step 1: Search RFPs */}
                      <VStack spacing={3}>
                        <HStack spacing={2} className="items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                            1
                          </div>
                          <h3 className="text-sm font-semibold text-foreground">Search RFPs</h3>
                        </HStack>
                        <HStack spacing={3} className="flex-wrap items-end">
                          <div className="flex-1 min-w-[200px]">
                            <Input
                              label="Search String"
                              hint="Enter search term to filter RFPs by name or code"
                              value={rfpSearchString}
                              onChange={(e) => setRfpSearchString(e.target.value)}
                              placeholder="e.g., mimecast"
                              leftIcon={<Search className="w-4 h-4" />}
                            />
                          </div>
                          <Button
                            onClick={() => fetchRfpListMutation.mutate(rfpSearchString || undefined)}
                            disabled={fetchRfpListMutation.isPending || !cventSettings?.browserCookies}
                            isLoading={fetchRfpListMutation.isPending}
                            leftIcon={<List className="w-4 h-4" />}
                            variant="primary"
                            size="md"
                          >
                            Fetch RFP List
                          </Button>
                        </HStack>

                        {fetchRfpListMutation.isPending && (
                          <Alert variant="info" title="Fetching RFPs...">
                            Please wait while we fetch the RFP list from Cvent.
                          </Alert>
                        )}

                        {rfpListResult && (
                          <VStack spacing={2}>
                            <HStack spacing={2} className="items-center justify-between">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <FileJson className="w-4 h-4" />
                                RFP List Response
                              </h4>
                              <Button
                                onClick={() => setRfpListResult(null)}
                                variant="ghost"
                                size="sm"
                              >
                                Clear
                              </Button>
                            </HStack>
                            <div className="w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 overflow-auto max-h-[400px]">
                              <pre className="text-xs font-mono text-neutral-800 whitespace-pre-wrap break-words">
                                {rfpListResult}
                              </pre>
                            </div>
                          </VStack>
                        )}
                      </VStack>

                      <div className="border-t border-neutral-200" />

                      {/* Step 2: Get RFP Details + Venues */}
                      <VStack spacing={3}>
                        <HStack spacing={2} className="items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                            2
                          </div>
                          <h3 className="text-sm font-semibold text-foreground">Get RFP Details & Venues</h3>
                        </HStack>
                        <HStack spacing={3} className="flex-wrap items-end">
                          <div className="flex-1 min-w-[200px]">
                            <Input
                              label="RFP ID"
                              hint="Enter the RFP ID from the list above"
                              value={rfpDetailsId}
                              onChange={(e) => setRfpDetailsId(e.target.value)}
                              placeholder="e.g., bf963e9d-21ce-4c61-b185-cbc907a5e04a"
                            />
                          </div>
                          <Button
                            onClick={() => {
                              if (rfpDetailsId.trim()) {
                                fetchRfpDetailsMutation.mutate(rfpDetailsId.trim());
                              }
                            }}
                            disabled={fetchRfpDetailsMutation.isPending || !cventSettings?.browserCookies || !rfpDetailsId.trim()}
                            isLoading={fetchRfpDetailsMutation.isPending}
                            leftIcon={<FileText className="w-4 h-4" />}
                            variant="primary"
                            size="md"
                          >
                            Fetch RFP Details
                          </Button>
                        </HStack>

                        {fetchRfpDetailsMutation.isPending && (
                          <Alert variant="info" title="Fetching RFP details...">
                            Please wait while we fetch the RFP details and venues list from Cvent.
                          </Alert>
                        )}

                        {rfpDetailsResult && (
                          <VStack spacing={2}>
                            <HStack spacing={2} className="items-center justify-between">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <FileJson className="w-4 h-4" />
                                RFP Details Response
                              </h4>
                              <Button
                                onClick={() => setRfpDetailsResult(null)}
                                variant="ghost"
                                size="sm"
                              >
                                Clear
                              </Button>
                            </HStack>
                            <div className="w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 overflow-auto max-h-[600px]">
                              <pre className="text-xs font-mono text-neutral-800 whitespace-pre-wrap break-words">
                                {rfpDetailsResult}
                              </pre>
                            </div>
                          </VStack>
                        )}
                      </VStack>

                      <div className="border-t border-neutral-200" />

                      {/* Step 3: Get Detailed Venue Response */}
                      <VStack spacing={3}>
                        <HStack spacing={2} className="items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                            3
                          </div>
                          <h3 className="text-sm font-semibold text-foreground">Get Detailed Venue Response</h3>
                        </HStack>
                        <HStack spacing={3} className="flex-wrap items-end">
                          <div className="flex-1 min-w-[200px]">
                            <Input
                              label="RFP ID"
                              hint="Enter the RFP ID"
                              value={venueResponseRfpId}
                              onChange={(e) => setVenueResponseRfpId(e.target.value)}
                              placeholder="e.g., bf963e9d-21ce-4c61-b185-cbc907a5e04a"
                            />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <Input
                              label="Supplier ID"
                              hint="Enter the supplier/venue ID (found in RFP details response)"
                              value={venueResponseSupplierId}
                              onChange={(e) => setVenueResponseSupplierId(e.target.value)}
                              placeholder="e.g., a0f9979b-eccf-4e14-9e51-825722045a75"
                            />
                          </div>
                          <Button
                            onClick={() => {
                              if (venueResponseRfpId.trim() && venueResponseSupplierId.trim()) {
                                fetchVenueResponseMutation.mutate({
                                  rfpId: venueResponseRfpId.trim(),
                                  supplierId: venueResponseSupplierId.trim(),
                                });
                              }
                            }}
                            disabled={fetchVenueResponseMutation.isPending || !cventSettings?.browserCookies || !venueResponseRfpId.trim() || !venueResponseSupplierId.trim()}
                            isLoading={fetchVenueResponseMutation.isPending}
                            leftIcon={<FileText className="w-4 h-4" />}
                            variant="primary"
                            size="md"
                          >
                            Fetch Venue Response
                          </Button>
                        </HStack>

                        {fetchVenueResponseMutation.isPending && (
                          <Alert variant="info" title="Fetching venue response...">
                            Please wait while we fetch the detailed venue response and proposal information from Cvent.
                          </Alert>
                        )}

                        {venueResponseResult && (
                          <VStack spacing={2}>
                            <HStack spacing={2} className="items-center justify-between">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <FileJson className="w-4 h-4" />
                                Venue Response (Detailed Proposal)
                              </h4>
                              <Button
                                onClick={() => setVenueResponseResult(null)}
                                variant="ghost"
                                size="sm"
                              >
                                Clear
                              </Button>
                            </HStack>
                            <div className="w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 overflow-auto max-h-[600px]">
                              <pre className="text-xs font-mono text-neutral-800 whitespace-pre-wrap break-words">
                                {venueResponseResult}
                              </pre>
                            </div>
                          </VStack>
                        )}
                      </VStack>

                      <div className="border-t border-neutral-200" />

                      {/* Step 4: Download Proposal PDF */}
                      <VStack spacing={3}>
                        <HStack spacing={2} className="items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                            4
                          </div>
                          <h3 className="text-sm font-semibold text-foreground">Download Proposal PDF</h3>
                        </HStack>
                        <VStack spacing={3}>
                          <HStack spacing={3} className="flex-wrap items-end">
                            <div className="flex-1 min-w-[200px]">
                              <Input
                                label="RFP ID"
                                hint="Enter the RFP ID"
                                value={proposalRfpId}
                                onChange={(e) => setProposalRfpId(e.target.value)}
                                placeholder="e.g., bf963e9d-21ce-4c61-b185-cbc907a5e04a"
                              />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <Input
                                label="Supplier ID"
                                hint="Enter the supplier/venue ID (found in RFP details response)"
                                value={proposalSupplierId}
                                onChange={(e) => setProposalSupplierId(e.target.value)}
                                placeholder="e.g., 4e60d41a-5633-4297-9f38-9c1ab5ffac1a"
                              />
      </div>
                            <Button
                              onClick={() => {
                                if (proposalRfpId.trim() && proposalSupplierId.trim()) {
                                  downloadProposalPdfMutation.mutate({
                                    rfpId: proposalRfpId.trim(),
                                    supplierId: proposalSupplierId.trim(),
                                  });
                                }
                              }}
                              disabled={downloadProposalPdfMutation.isPending || !cventSettings?.browserCookies || !proposalRfpId.trim() || !proposalSupplierId.trim()}
                              isLoading={downloadProposalPdfMutation.isPending}
                              leftIcon={<Download className="w-4 h-4" />}
                              variant="primary"
                              size="md"
                            >
                              Download PDF
                            </Button>
                          </HStack>

                          {downloadProposalPdfMutation.isPending && (
                            <Alert variant="info" title="Downloading proposal PDF...">
                              Please wait while we download the proposal PDF from Cvent.
                            </Alert>
                          )}

                          {downloadProposalPdfMutation.isError && (
                            <Alert variant="error" title="Download Failed">
                              {downloadProposalPdfMutation.error?.message || 'Failed to download proposal PDF. Please check the RFP ID and Supplier ID.'}
                            </Alert>
                          )}
                        </VStack>
                      </VStack>
                    </VStack>
                  </CardContent>
                </Card>
              </VStack>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manifest-prompts" className="space-y-6">
          <Card>
            <CardHeader>
              <HStack spacing={2} className="items-center">
                <FileCode className="w-5 h-5 text-primary-600" />
                <VStack spacing={0} className="flex-1">
                  <CardTitle>Manifest Prompts</CardTitle>
                  <CardDescription>
                    Edit the prompts used for PDF extraction and manifest review. Each edit creates a new version; you can revert to any previous version.
                  </CardDescription>
                </VStack>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => seedManifestPromptsMutation.mutate()}
                  disabled={seedManifestPromptsMutation.isPending}
                  isLoading={seedManifestPromptsMutation.isPending}
                >
                  Seed Initial Prompts
                </Button>
              </HStack>
            </CardHeader>
            <CardContent>
              {isLoadingManifestPrompts ? (
                <VStack spacing={4} className="items-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  <span className="text-sm text-muted-foreground">Loading prompts...</span>
                </VStack>
              ) : isManifestPromptsError ? (
                <Alert variant="error">Failed to load prompts. You may need super admin access.</Alert>
              ) : manifestPrompts ? (
                <VStack spacing={8}>
                  {/* Extraction Prompt */}
                  <ManifestPromptEditor
                    title="Extraction Prompt"
                    description="Used when extracting passenger and flight data from uploaded PDFs."
                    promptKey="extraction"
                    data={manifestPrompts.extraction}
                    onUpdate={(content) => updateManifestPromptMutation.mutate({ key: 'extraction', content })}
                    onRevert={(version) => revertManifestPromptMutation.mutate({ key: 'extraction', version })}
                    isUpdating={updateManifestPromptMutation.isPending}
                    isReverting={revertManifestPromptMutation.isPending}
                  />
                  <div className="border-t border-neutral-200" />
                  {/* Review Prompt */}
                  <ManifestPromptEditor
                    title="Review Prompt (Editable Rules)"
                    description="The rules the AI uses to identify issues. Only this list is editable; records, attendees, and output format are inserted by the system."
                    promptKey="review"
                    data={manifestPrompts.review}
                    onUpdate={(content) => updateManifestPromptMutation.mutate({ key: 'review', content })}
                    onRevert={(version) => revertManifestPromptMutation.mutate({ key: 'review', version })}
                    isUpdating={updateManifestPromptMutation.isPending}
                    isReverting={revertManifestPromptMutation.isPending}
                  />
                </VStack>
              ) : (
                <Alert variant="info">Switch to this tab to load prompts.</Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function ManifestPromptEditor({
  title,
  description,
  promptKey,
  data,
  onUpdate,
  onRevert,
  isUpdating,
  isReverting,
}: {
  title: string;
  description: string;
  promptKey: 'extraction' | 'review';
  data: { current: { content: string; version: number }; versions: Array<{ id: string; version: number; content: string; created_at: string }> };
  onUpdate: (content: string) => void;
  onRevert: (version: number) => void;
  isUpdating: boolean;
  isReverting: boolean;
}) {
  const [editingContent, setEditingContent] = useState(data.current.content);
  const [hasChanges, setHasChanges] = useState(false);

  React.useEffect(() => {
    setEditingContent(data.current.content);
    setHasChanges(false);
  }, [data.current.content, data.current.version]);

  const handleContentChange = (value: string) => {
    setEditingContent(value);
    setHasChanges(value !== data.current.content);
  };

  return (
    <VStack spacing={4}>
      <VStack spacing={1}>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
        <HStack spacing={2} className="items-center">
          <Badge variant="secondary">v{data.current.version}</Badge>
          {data.versions.length > 1 && (
            <span className="text-xs text-muted-foreground">{data.versions.length} version(s) in history</span>
          )}
        </HStack>
      </VStack>
      <Textarea
        label=""
        value={editingContent}
        onChange={(e) => handleContentChange(e.target.value)}
        disabled={isUpdating}
        rows={12}
        className="font-mono text-sm"
      />
      <HStack spacing={3}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onUpdate(editingContent)}
          disabled={!hasChanges || isUpdating}
          isLoading={isUpdating}
        >
          Save (creates new version)
        </Button>
        {data.versions.length > 1 && (
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <select
              key={data.current.version}
              className="text-sm border rounded px-2 py-1"
              defaultValue=""
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (v && v !== data.current.version) onRevert(v);
              }}
              disabled={isReverting}
            >
              <option value="">Revert to...</option>
              {data.versions.slice(1).map((v) => (
                <option key={v.id} value={v.version}>
                  v{v.version} — {new Date(v.created_at).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
        )}
      </HStack>
    </VStack>
  );
}

